// @ts-nocheck
/* eslint no-underscore-dangle: 0 */
import type {SplinePoints} from '@Libraries/splines-library';
import {getPath} from '@Libraries/splines-library';
import {getScaleForFitToContainer, roundPrecision} from '@Utils/math.util';
import {loadImageAsync, loadImageSync} from '@Utils/image.util';
import {rgbToHexString} from '@Utils/color.util';
import type {Point} from '@Utils/math.util';
import type {CropData} from '@PosterWhiteboard/classes/item-crop.class';
import {MaskingEffect} from '@PosterWhiteboard/classes/masking/masking.class';
import {Canvas, Circle, FabricImage, filters} from '@postermywall/fabricjs-2';
import type {BaseFabricObject, TPointerEvent, Transform, CanvasEvents, FabricObject, Path, Point as FabricPoint} from '@postermywall/fabricjs-2';
import type {MaskingFreehandObject} from '@PosterWhiteboard/classes/masking/masking-freehand.class';
import type {MaskOptsInterface} from '@Libraries/mask';
import {KeyboardShortcutScope} from '@Libraries/keyboard-shortcuts';
import {setShowRemoveMask} from '@Panels/masking-panel/masking-panel-reducer';
import {setColorPop, setCurvedJoints, setRedo, setUndo} from '@Panels/masking-panel/components/freehand-masking-panel/freehand-masking-panel-reducer';
import {isMobile} from 'react-device-detect';

/**
 * Opacity of the background image
 */
const BACKGROUND_IMAGE_OPACITY = 0.2;
/**
 * Color of circle points
 */
const POINT_COLOR = 'rgba(63,188,231,1)';
/**
 * Color of clip path drawn
 */
const CLIP_PATH_COLOR = 'rgba(63,188,231,1)';
/**
 * Max zoom steps allowed
 */
const MAX_ZOOM_STEPS = 5;
/**
 * What ratio of default zoom should each zoom step have
 * should be greater than zoom steps
 */
const ZOOM_STEP_RATIO = MAX_ZOOM_STEPS + 2;

export interface ObjectModifiedEvent {
  e: TPointerEvent;
  target: FabricObject;
  transform: Transform;
  action: string;
}

export interface PointObject extends Point {
  view: FabricObject;
  radius: number;
}

export enum FreehandMaskingActions {
  ADD = 'add',
  CLOSE = 'close',
  MOVE = 'move',
  CLEAR = 'clear',
}

export interface UndoStackFrameData extends Partial<PointObject> {
  left?: number;
  top?: number;
}

export interface UndoStackFrame {
  action: FreehandMaskingActions;
  data?: UndoStackFrameData | Array<UndoStackFrameData>;
  c?: boolean;
  sp?: boolean;
}

type RedoStackFrame = Partial<UndoStackFrame>;

export class PointMask {
  public id = '';
  public canvas!: Canvas;
  public points: Array<PointObject> = [];
  public undoStack: Array<UndoStackFrame> = [];
  public redoStack: Array<RedoStackFrame> = [];
  public scale = 1;
  public showPreview = false;
  public isMaskCurved = true;
  public maskEffect = MaskingEffect.NONE;
  public isPathClosed = false;
  public pointRadius = 5;

  public cropData?: CropData = undefined;
  public loader = document.createElement('img');
  protected keyboardShortcutScope = KeyboardShortcutScope.ITEM_POINT_MASKING; // TODO

  public imageWidth = 0;
  public imageHeight = 0;
  public backGroundImage!: FabricImage;
  public imageToMask!: FabricImage;
  public scaleForFitToScreen = 1;
  public splinePath?: Path;
  public mouseDownPosition!: FabricPoint;

  private parentSelector = '';
  private url = '';
  private initialMaskingData?: MaskingFreehandObject = undefined;

  public constructor(id: string, url: string, opts: MaskOptsInterface, parentSelector: string) {
    if (!id || !url) {
      return;
      // throw new Error('Specify a valid id for mask');
    }

    this.initializeClassVariables(id, url, opts, parentSelector);
  }

  public async init(): Promise<void> {
    this.loader = await loadImageAsync(this.url);
    this.onImageLoaded();
  }

  private initializeClassVariables(id: string, url: string, opts: MaskOptsInterface, selector: string): void {
    this.id = id;

    if (isMobile) {
      this.pointRadius = 12;
    }

    this.cropData = opts.cropData;
    this.url = url;
    this.parentSelector = selector;
    this.initialMaskingData = opts.initialMaskingData as MaskingFreehandObject | undefined;
  }

  public async dispose(): Promise<void> {
    await this.canvas.dispose();
  }

  private onImageLoaded(): void {
    const c = document.getElementById(this.id) as HTMLCanvasElement;

    c.width = this.cropData ? this.cropData.width : this.loader.width;
    c.height = this.cropData ? this.cropData.height : this.loader.height;
    this.imageWidth = c.width;
    this.imageHeight = c.height;

    this.canvas = new Canvas(c, {
      hoverCursor: 'pointer',
      selection: false,
      controlsAboveOverlay: true,
      allowTouchScrolling: true,
      preserveObjectStacking: true,
      perPixelTargetFind: false,
    });

    this.backGroundImage = new FabricImage(this.loader, {
      width: this.cropData ? this.cropData.width : this.canvas.width,
      height: this.cropData ? this.cropData.height : this.canvas.height,
      cropX: this.cropData ? this.cropData.x : 0,
      cropY: this.cropData ? this.cropData.y : 0,
      originX: 'left',
      originY: 'top',
      opacity: BACKGROUND_IMAGE_OPACITY,
    });
    this.canvas.requestRenderAll();

    this.canvas.on('mouse:up', this.onMouseUp.bind(this));
    this.canvas.on('mouse:down', this.onMouseDown.bind(this));
    this.canvas.on('object:moving', this.onObjectMoving.bind(this));
    this.canvas.on('selection:updated', this.onObjectSelected.bind(this));
    this.canvas.on('selection:created', this.onObjectSelected.bind(this));
    this.canvas.on('object:modified', this.onObjectModified.bind(this));

    this.imageToMask = new FabricImage(this.loader, {
      width: this.cropData ? this.cropData.width : this.loader.width,
      height: this.cropData ? this.cropData.height : this.loader.height,
      cropX: this.cropData ? this.cropData.x : 0,
      cropY: this.cropData ? this.cropData.y : 0,
      lockRotation: true,
      lockScalingX: true,
      lockScalingY: true,
      lockMovementX: true,
      lockMovementY: true,
      hasControls: false,
      hasBorders: false,
      padding: 0,
    });
    this.canvas.add(this.imageToMask);

    this.setInitialZoom_();
    this.restoreMask_();
  }

  private setInitialZoom_(): void {
    const htmlElement: HTMLElement = $(`#${this.id}`).parents(this.parentSelector);
    const availableWidth = htmlElement.width() - 5;
    const availableHeight = htmlElement.height() - 5;

    this.scaleForFitToScreen = getScaleForFitToContainer(availableWidth, availableHeight, this.imageWidth, this.imageHeight);
    this.setZoom(this.scaleForFitToScreen);
  }

  public increaseZoom(): void {
    if (!this.isMaxZoomed()) {
      this.setZoom(this.getScale() + this.getZoomStep());
    }
  }

  public decreaseZoom(): void {
    if (!this.isMinZoomed()) {
      this.setZoom(this.getScale() - this.getZoomStep());
    }
  }

  public isMaxZoomed(): boolean {
    return this.getScale() >= this.scaleForFitToScreen + this.getZoomStep() * MAX_ZOOM_STEPS;
  }

  public isMinZoomed(): boolean {
    return this.getScale() <= this.scaleForFitToScreen - this.getZoomStep() * MAX_ZOOM_STEPS;
  }

  private getZoomStep(): number {
    return roundPrecision(this.scaleForFitToScreen / ZOOM_STEP_RATIO, 2);
  }

  public setBackgroundImage(url: string): void {
    loadImageSync(url, this.onBackgroundImageLoaded_.bind(this), (): void => {});
  }

  private onBackgroundImageLoaded_(img: HTMLImageElement): void {
    this.imageToMask.setElement(img);
    if (this.canvas.backgroundImage instanceof FabricImage) {
      this.canvas.backgroundImage.setElement(img);
    }
    this.invalidate();
  }

  public invalidate(): void {
    this.onShowPreview();
    this.canvas.renderAll();

    const {scale} = this;
    // Set zoom to 1 temporarily to get the unscaled points for getting the path
    this.setZoom(1);

    this.drawPath_(getPath(this.points, this.isPathClosed, this.isMaskCurved));
    this.setZoom(scale);

    const currentRemoveMaskState = window.PMW.redux.store.getState().masking.showRemoveMask;
    if (currentRemoveMaskState !== this.hasMaskingPoints()) {
      window.PMW.redux.store.dispatch(setShowRemoveMask(this.hasMaskingPoints()));
    }

    const currentUndoState = window.PMW.redux.store.getState().freehandMasking.disableUndo;
    if (currentUndoState !== !this.isUndoAvailable()) {
      window.PMW.redux.store.dispatch(setUndo(!this.isUndoAvailable()));
    }
    const currentRedoState = window.PMW.redux.store.getState().freehandMasking.disableRedo;
    if (currentRedoState !== !this.isRedoAvailable()) {
      window.PMW.redux.store.dispatch(setRedo(!this.isRedoAvailable()));
    }

    const currentJointsCurvedState = window.PMW.redux.store.getState().freehandMasking.curvedJoints;
    if (currentJointsCurvedState !== this.isMaskCurved) {
      window.PMW.redux.store.dispatch(setCurvedJoints(this.isMaskCurved));
    }

    const currentColorPopState = window.PMW.redux.store.getState().freehandMasking.colorPop;
    const isColorPop = this.maskEffect === MaskingEffect.COLOR_POP;
    if (currentColorPopState !== isColorPop) {
      window.PMW.redux.store.dispatch(setColorPop(isColorPop));
    }
  }

  private drawPath_(path: Path): void {
    if (this.splinePath) {
      this.canvas.remove(this.splinePath);
    }

    if (path) {
      path.set({
        strokeWidth: 1 / this.scaleForFitToScreen,
        stroke: CLIP_PATH_COLOR,
        strokeUniform: true,
        opacity: 1,
        fill: rgbToHexString([0, 0, 0], 0),
        selectable: false,
        evented: false,
      });

      this.canvas.add(path);
      this.splinePath = path;
    }
  }

  private getPointsForMaskedArea_(): Array<Point> {
    const points = this.getPoints();
    const newPoints: Array<Point> = [];
    const scale = this.getScale();
    const {width, height} = this.imageToMask;

    for (let i = 0; i < points.length; i++) {
      newPoints.push({
        x: points[i].x / scale - width / 2,
        y: points[i].y / scale - height / 2,
      });
    }

    return newPoints;
  }

  public removeAllPoints(): void {
    this.addToUndoStack(FreehandMaskingActions.CLEAR, {
      p: this.points,
      c: this.isPathClosed,
      sp: this.showPreview,
    });
    this.redoStack.length = 0;
    for (let i = 0; i < this.points.length; i++) {
      this.points[i].view.visible = false;
    }
    this.points.forEach((point) => {
      point.view.visible = false;
    });
    this.resetFlags();
    this.points.length = 0;
    this.invalidate();
  }

  public getPoints(): Array<Point> {
    return this.points;
  }

  public getScale(): number {
    return this.scale;
  }

  public getMaskType(): boolean {
    return this.isMaskCurved;
  }

  public getMaskEffect(): MaskingEffect {
    return this.maskEffect;
  }

  public setMaskCurved(value: boolean): void {
    this.isMaskCurved = value;
    this.invalidate();
  }

  public setMaskEffect(value: MaskingEffect): void {
    this.maskEffect = value;
    this.onMaskEffect();
  }

  public onMaskEffect(): void {
    if (this.maskEffect === MaskingEffect.COLOR_POP) {
      // apply the greyscale filter
      this.backGroundImage.opacity = 1;
      this.backGroundImage.filters.push(new filters.Grayscale());
    } else {
      // normal state
      this.backGroundImage.filters.length = 0;
      this.backGroundImage.opacity = BACKGROUND_IMAGE_OPACITY;
    }
    this.backGroundImage.applyFilters();
    this.invalidate();
  }

  public setZoom(value: number): void {
    const precision = roundPrecision(value, 2);
    // calculate multiplier for scaling the points.
    const multiplier = precision / this.scale;
    this.scale = precision;
    this.pointRadius /= multiplier;
    this.canvas.setDimensions({
      width: this.canvas.width * multiplier,
      height: this.canvas.height * multiplier,
    });

    // setting zoom
    this.canvas.setZoom(this.scale);
    // scaling points
    this.scalePoints_(multiplier);
  }

  private onShowPreview(): void {
    if (this.isPathClosed && this.points.length > 2) {
      const newPoints = this.getPointsForMaskedArea_();
      this.imageToMask.set({
        clipPath: this.getMaskedArea_(newPoints),
      });
    } else {
      this.imageToMask.set({
        clipPath: null,
      });
    }
  }

  public onUndo(): void {
    if (this.undoStack.length > 0) {
      const cmd = this.undoStack.pop();
      switch (cmd.action) {
        case FreehandMaskingActions.ADD:
          // remove the points from points_ array and canvas
          this.canvas.remove(cmd.data.view as BaseFabricObject);
          this.points.pop();
          break;

        case FreehandMaskingActions.MOVE:
          // get the most recent version of this point in the undo stack, and set the model and view to its values
          for (let i = this.undoStack.length - 1; i >= 0; i--) {
            if (this.undoStack[i].data != null && this.undoStack[i].data.view.__idPoint === cmd.data.view.__idPoint) {
              // Revert the entry in points_ to these old coordinates
              const point = this.getPointByView(cmd.data.view);
              point.x = this.undoStack[i].data.x;
              point.y = this.undoStack[i].data.y;

              // update the view (this is a reference to the fabric.Rect object in fabric.Canvas)
              point.view.set({
                left: this.undoStack[i].data.left,
                top: this.undoStack[i].data.top,
              });
              point.view.setCoords();
              break;
            }
          }
          break;

        case FreehandMaskingActions.CLOSE:
          // open the path and hide the preview
          this.isPathClosed = false;
          this.showPreview = false;
          this.onShowPreview();
          break;

        case FreehandMaskingActions.CLEAR:
          // restore the previous state
          this.points = cmd.data;

          for (let x = 0; x < this.points.length; x++) {
            this.points[x].view.visible = true;
          }
          this.isPathClosed = cmd.c;
          this.showPreview = cmd.sp;
          break;
        default:
          throw new Error('Unexpected undo action for masking');
      }

      // add the command to redo stack
      this.addToRedoStack_(cmd.action, cmd.data);
      this.invalidate();
    }
  }

  public isUndoAvailable(): boolean {
    return this.undoStack.length !== 0;
  }

  public onRedo(): void {
    if (this.redoStack.length > 0) {
      const cmd: RedoStackFrame = this.redoStack.pop();
      switch (cmd.action) {
        case FreehandMaskingActions.ADD:
          // add the point back on canvas and in points_ array
          this.canvas.add(cmd.data.view);
          this.addPoint(Number(cmd.data.view.left) + Number(cmd.data.radius), Number(cmd.data.view.top) + Number(cmd.data.radius), cmd.data.view);
          break;
        case FreehandMaskingActions.MOVE: {
          // TODO: refactor this into a function
          const point = this.getPointByView(cmd.data.view);
          point.x = cmd.data.x;
          point.y = cmd.data.y;

          // update the view (this is a reference to the fabric.Circle object in fabric.Canvas)
          point.view.set({
            left: cmd.data.left,
            top: cmd.data.top,
          });
          point.view.setCoords();
          break;
        }
        case FreehandMaskingActions.CLOSE:
          // close the path again and show preview
          this.isPathClosed = true;
          this.showPreview = true;
          break;
        case FreehandMaskingActions.CLEAR:
          // remove all points from canvas and from points_ array
          this.removeAllPoints();
          break;
        default:
          throw new Error('Unexpected action in redo flow');
      }
      // undoStack is already updated in case of clear command,  so no need to push it again here
      if (cmd.action !== FreehandMaskingActions.CLEAR) {
        this.undoStack.push(cmd);
      }
      this.invalidate();
    }
  }

  public isRedoAvailable(): boolean {
    return this.redoStack.length !== 0;
  }

  public hasMaskingPoints(): boolean {
    return this.points.length > 0;
  }

  private resetFlags(): void {
    this.isPathClosed = false;
    this.showPreview = false;
  }

  private getMaskedArea_(points: SplinePoints): Path {
    // ask hamza
    return getPath(points, this.isPathClosed, this.isMaskCurved);
  }

  private getPointByView(view: FabricObject): PointObject | null {
    if (view.__idPoint != null && this.points.length > view.__idPoint) {
      return this.points[view.__idPoint as number];
    }
    return null;
  }

  private trigger_(event: string): void {
    $(`#${this.id}`).trigger(event);
  }

  public on(event: any, cb: () => void): void {
    $(`#${this.id}`).on(event, cb);
  }

  private addToUndoStack(
    action: FreehandMaskingActions,
    data: Partial<UndoStackFrameData> & {
      p: Array<PointObject>;
      c: boolean;
      sp: boolean;
    }
  ): void {
    // if last added command to undoStack is clear or close, then do not add it again to undoStack.
    if (
      this.undoStack.length > 0 &&
      ((this.undoStack[this.undoStack.length - 1].action === FreehandMaskingActions.CLOSE && action === FreehandMaskingActions.CLOSE) ||
        (this.undoStack[this.undoStack.length - 1].action === FreehandMaskingActions.CLEAR && action === FreehandMaskingActions.CLEAR))
    ) {
      return;
    }

    this.trigger_('addToUndoStack');
    switch (action) {
      case FreehandMaskingActions.CLOSE:
        this.undoStack.push({
          action,
        });
        break;

      case FreehandMaskingActions.ADD:
      case FreehandMaskingActions.MOVE: {
        const clone = {
          x: data.x,
          y: data.y,
          view: data.view,
          radius: data.radius,
          left: data.view.left,
          top: data.view.top,
        };
        this.undoStack.push({
          action,
          data: clone,
        });
        break;
      }

      case FreehandMaskingActions.CLEAR: {
        const pts = [];
        for (let i = 0; i < data.p.length; i++) {
          pts.push({
            x: data.p[i].x,
            y: data.p[i].y,
            view: data.p[i].view,
            radius: data.p[i].radius,
          });
        }
        this.undoStack.push({
          action,
          data: pts,
          c: data.c,
          sp: data.sp,
        });
        break;
      }
      default:
        throw new Error('Unexpected action for Undo-able stack frame');
    }
  }

  private addToRedoStack_(action: FreehandMaskingActions, data: UndoStackFrameData): void {
    switch (action) {
      case FreehandMaskingActions.CLOSE:
        if (!this.isPathClosed) {
          // check for pathClose flag, if its already true then do not add the close action to undoStack
          this.redoStack.push({
            action,
          });
        }
        break;
      case FreehandMaskingActions.ADD:
      case FreehandMaskingActions.MOVE:
        this.redoStack.push({
          action,
          data,
        });
        break;
      case FreehandMaskingActions.CLEAR:
        this.redoStack.push({
          action,
          data: null,
        });
        break;
      default:
        throw new Error('Unexpected undoa action for masking');
    }
  }

  private scalePoints_(multiplier: number): void {
    for (let i = 0; i < this.points.length; i++) {
      this.points[i].x *= multiplier;
      this.points[i].y *= multiplier;
      this.points[i].view.radius /= multiplier;
    }
    // TODO: review the below code, move it into a function
    for (let x = 0; x < this.redoStack.length; x++) {
      switch (this.redoStack[x].action) {
        case FreehandMaskingActions.ADD:
        case FreehandMaskingActions.MOVE:
          this.redoStack[x].data.x *= multiplier;
          this.redoStack[x].data.y *= multiplier;
          break;
        default:
          break;
      }
    }
    for (let y = 0; y < this.undoStack.length; y++) {
      switch (this.undoStack[y].action) {
        case FreehandMaskingActions.ADD:
        case FreehandMaskingActions.MOVE:
          this.undoStack[y].data.x *= multiplier;
          this.undoStack[y].data.y *= multiplier;
          break;
        case FreehandMaskingActions.CLEAR:
          for (let d = 0; d < this.undoStack[y].data.length; d++) {
            this.undoStack[y].data[d].x *= multiplier;
            this.undoStack[y].data[d].y *= multiplier;
            this.undoStack[y].data[d].view.radius /= multiplier;
          }
          break;
        default:
          break;
      }
    }
  }

  private restoreMask_(): void {
    if (this.initialMaskingData) {
      this.isMaskCurved = this.initialMaskingData.isMaskCurved;
      this.maskEffect = this.initialMaskingData.maskEffect;
      for (let i = 0; i < this.initialMaskingData.maskPoints.length; i++) {
        this.addToUndoStack(
          'add',
          this.addPoint(
            this.initialMaskingData.maskPoints[i].x * this.getScaleX(),
            this.initialMaskingData.maskPoints[i].y * this.getScaleY(),
            this.drawPoint(this.initialMaskingData.maskPoints[i].x * this.getScaleX(), this.initialMaskingData.maskPoints[i].y * this.getScaleY())
          )
        );
      }
      this.addToUndoStack('close', null);
      this.isPathClosed = true;
      this.showPreview = true;
      this.onMaskEffect();
      this.invalidate();
    }
  }

  private getScaleX(): number {
    return this.initialMaskingData ? this.imageWidth / this.initialMaskingData.imageWidth : 1;
  }

  private getScaleY(): number {
    return this.initialMaskingData ? this.imageHeight / this.initialMaskingData.imageHeight : 1;
  }

  private drawPoint(left: any, top: any): Circle {
    const circle = new Circle({
      radius: this.pointRadius,
      left: left - this.pointRadius,
      top: top - this.pointRadius,
      angle: 0,
      stroke: POINT_COLOR,
      strokeWidth: 1 / this.scaleForFitToScreen,
      strokeUniform: true,
      fill: 'rgba(255,255,255,0.2)',
      lockRotation: true,
      lockScalingX: true,
      lockScalingY: true,
      hasControls: false,
      hasBorders: false,
      padding: 0,
      hoverCursor: 'move',
    });

    this.canvas.add(circle);
    return circle;
  }

  private addPoint(x: number, y: number, circle: Circle): PointObject {
    const point: PointObject = {
      x: x * this.scale,
      y: y * this.scale,
      view: circle,
      radius: this.pointRadius,
    };

    point.view.__idPoint = this.points.length;
    this.points.push(point);
    return point;
  }

  private onMouseDown(o: CanvasEvents['mouse:down']): void {
    if (o.target instanceof Circle) {
      this.canvas.allowTouchScrolling = false;
    }
    this.mouseDownPosition = this.canvas.getScenePoint(o.e);
  }

  private onMouseUp(o: CanvasEvents['mouse:up']): void {
    if (typeof o.target === 'undefined' || o.target instanceof FabricImage) {
      const point = this.canvas.getScenePoint(o.e);
      const left = point.x;
      const top = point.y;

      // if user is on touch device then only add point if mouse down and mouse up positions are same
      if (isMobile) {
        // if mouse up position is not same as mouse down position means user is dragging.
        // hence do not add point.
        if (Math.abs(this.mouseDownPosition.x - left) < 30 && Math.abs(this.mouseDownPosition.y - top) < 30) {
          // draw point, add to array of points and add to undoStack
          this.addToUndoStack(FreehandMaskingActions.ADD, this.addPoint(left, top, this.drawPoint(left, top)));
          // clear the redo stack
          this.redoStack.length = 0;
        }
      } else {
        // draw point, add to array of points and add to undoStack
        this.addToUndoStack(FreehandMaskingActions.ADD, this.addPoint(left, top, this.drawPoint(left, top)));
        // clear the redo stack
        this.redoStack.length = 0;
      }
    }
    this.canvas.allowTouchScrolling = true;
    this.invalidate();
  }

  private onObjectMoving(o: CanvasEvents['object:moving']): void {
    const pts = this.getPoints();
    for (let i = 0; i < pts.length; i++) {
      if (pts[i].view === o.target) {
        const radiusOffset = pts[i].radius * this.scale;
        pts[i].x = o.target.left * this.getScale() + radiusOffset;
        pts[i].y = o.target.top * this.getScale() + radiusOffset;
      }
    }
    this.invalidate();
  }

  private onObjectModified(o: CanvasEvents['object:modified']): void {
    if (o.target) {
      const point = this.getPointByView(o.target);
      if (point != null) {
        this.addToUndoStack(FreehandMaskingActions.MOVE, point);
      }
      this.redoStack.length = 0;
    }
  }

  private onObjectSelected(o: any): void {
    if (this.points.length > 2 && this.points[0] != null && o.selected.length === 1 && o.selected[0] === this.points[0].view && !this.isPathClosed) {
      this.showPreview = true;
      this.canvas.discardActiveObject();
      // eslint-disable-next-line no-underscore-dangle
      this.addToUndoStack('close', null);
      this.isPathClosed = true;
      this.redoStack.length = 0;
      this.invalidate();
    }
  }
}

export const createPointMask = async (id: string, url: string, parentSelector: string, opts: MaskOptsInterface): Promise<PointMask> => {
  const pointMask = new PointMask(id, url, opts, parentSelector);
  await pointMask.init();
  return pointMask;
};
